import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { useAuth } from "@/hooks/useAuth";
import { login as backendLogin } from "@/integrations/backend/auth";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { hasTokens } from "@/lib/token-storage";
import frequentHealthIcon from "@/assets/frequent-health-icon.jpeg";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, refetchProfile, loading, sessionChecked } = useAuth();

  // If already logged in and we land on /login → go to PostLogin for a single decision
  useEffect(() => {
    try { console.debug("[login] user state", { hasUser: !!user }); } catch {}
    if (!sessionChecked && hasTokens()) {
      // Optimistic redirect to avoid flashing login UI while auth bootstraps.
      navigate("/post-login", { replace: true });
      return;
    }
    if (sessionChecked && user) navigate("/post-login", { replace: true });
  }, [user, sessionChecked, navigate]);

  // Don't show the login form until we know whether the user is already authenticated.
  if (loading || !sessionChecked) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
        <div className="text-white text-lg">Checking your session…</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await backendLogin(email, password);
      // Refetch profile after successful login to update auth state
      await refetchProfile();
      toast.success("Welcome back!");
      navigate("/post-login", { replace: true });
    } catch (error: unknown) {
      toast.error(getAuthErrorMessage("login", error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MarketingHeader />
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4 pt-24">
      <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img
              src={frequentHealthIcon}
              alt="Frequent Health logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl text-white">Welcome Back</CardTitle>
          <p className="text-gray-light">Sign in to continue your journey</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-light">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-light">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
              />
            </div>

            <div className="flex justify-end -mt-2">
              <Link to="/reset-password" className="text-sm text-primary hover:text-lime-hover">
                Forgot your password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all"
            >
              {isSubmitting ? "Signing In..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-light text-sm">
              Don't have an account?{" "}
              <Link to="/signup" className="text-primary hover:text-lime-hover font-medium">
                Sign Up
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-gray-light hover:text-white text-sm">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
      </div>
      <MarketingFooter />
    </>
  );
}
